/**
 * Advertising Optimization Arche Page — route wrapper.
 *
 * Mounted at /a/arche_advertising_optimization/*. Holder-scoped via the
 * shared <HolderSwitcher>; empty-state until a workspace is picked
 * (fias-leads pattern).
 *
 * Sub-routes:
 *   /                       → DashboardPage
 *   /campaigns              → CampaignsListPage
 *   /campaigns/new          → CampaignWizardPage
 *   /campaigns/:campaignId  → CampaignDetailPage
 *   /experiments            → ExperimentsPage
 *   /experiments/:experimentId → ExperimentDetailPage
 *   /links                  → LinksPage
 *   /settings               → SettingsPage
 */

import { Routes, Route } from 'react-router-dom';
import { ArcheLayout } from '../../../components/ArcheLayout';
import { ArcheHeader } from '../../../components/ArcheHeader';
import { HolderSwitcher } from '@/components/HolderSwitcher';
import { ARCHE_ID } from '../constants';
import { useSelectedHolder } from '../hooks/useSelectedHolder';
import { useAdoptAccess } from '../hooks/useAdvertisingOptimization';
import { AdoptNav } from '../components/AdoptNav';
import { DashboardPage } from './DashboardPage';
import { CampaignsListPage } from './CampaignsListPage';
import { CampaignWizardPage } from './CampaignWizardPage';
import { CampaignDetailPage } from './CampaignDetailPage';
import { ExperimentsPage } from './ExperimentsPage';
import { ExperimentDetailPage } from './ExperimentDetailPage';
import { ActionInboxPage } from './ActionInboxPage';
import { LinksPage } from './LinksPage';
import { SettingsPage } from './SettingsPage';

function NoHolderEmptyState({
  holderId,
  setHolderId,
}: {
  holderId: string | null;
  setHolderId: (id: string | null) => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
      <h2 className="text-xl font-semibold text-foreground">Choose a workspace to get started</h2>
      <p className="max-w-md text-muted-foreground">
        Advertising Optimization runs ad campaigns for a workspace — its campaigns, budgets, and
        attribution data are scoped to the Holder you pick.
      </p>
      <HolderSwitcher selectedHolderId={holderId} onChange={(id) => setHolderId(id)} />
    </div>
  );
}

/**
 * Shown in a workspace the caller may not use AdOpt in. Matches the server gate
 * (routes/arches/advertising-optimization/index.ts + services/adopt-access.ts):
 * without it every API call underneath would 403, which reads as "the arche is
 * broken" rather than "not available here".
 */
function NotAvailableHere() {
  return (
    <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
      <h2 className="text-xl font-semibold text-foreground">Not available in this workspace</h2>
      <p className="max-w-md text-muted-foreground">
        Advertising Optimization is open only in workspaces a platform administrator has enabled it
        for. Pick another workspace, or ask an administrator to enable this one.
      </p>
    </div>
  );
}

/** The arche's pages, once the caller may use AdOpt in the picked workspace. */
function AdoptRoutes({ holderId }: { holderId: string }) {
  const accessQuery = useAdoptAccess(holderId);
  if (accessQuery.isLoading) {
    return <div className="p-6 text-muted-foreground">Loading…</div>;
  }
  if (!accessQuery.data?.allowed) return <NotAvailableHere />;
  return (
    <Routes>
      <Route path="/" element={<DashboardPage holderId={holderId} />} />
      <Route path="/campaigns" element={<CampaignsListPage holderId={holderId} />} />
      <Route path="/campaigns/new" element={<CampaignWizardPage holderId={holderId} />} />
      <Route path="/campaigns/:campaignId" element={<CampaignDetailPage holderId={holderId} />} />
      <Route path="/experiments" element={<ExperimentsPage holderId={holderId} />} />
      <Route
        path="/experiments/:experimentId"
        element={<ExperimentDetailPage holderId={holderId} />}
      />
      <Route path="/actions" element={<ActionInboxPage holderId={holderId} />} />
      <Route path="/links" element={<LinksPage holderId={holderId} />} />
      <Route path="/settings" element={<SettingsPage holderId={holderId} />} />
    </Routes>
  );
}

function AdvertisingOptimizationArcheContent() {
  const { holderId, setHolderId } = useSelectedHolder();
  const access = useAdoptAccess(holderId).data;

  return (
    <ArcheLayout>
      <div className="flex flex-col h-full">
        <ArcheHeader archeId={ARCHE_ID} title="AdOpt - Advertising Optimization">
          <HolderSwitcher selectedHolderId={holderId} onChange={(id) => setHolderId(id)} />
        </ArcheHeader>
        {/* Focus-mode restore is handled centrally by ArcheLayout's expanded
            header (Back-to-Fias + Fias AI + Collapse) — no per-arche control. */}
        {holderId && access?.allowed && <AdoptNav />}
        <div className="flex-1 overflow-auto">
          {!holderId ? (
            <NoHolderEmptyState holderId={holderId} setHolderId={setHolderId} />
          ) : (
            <AdoptRoutes holderId={holderId} />
          )}
        </div>
      </div>
    </ArcheLayout>
  );
}

/**
 * Who sees what is decided per workspace by GET /access — a platform admin
 * anywhere, anyone else only in a workspace a platform admin opened. This is
 * a UX gate, NOT the security boundary: the server gate is.
 */
export function AdvertisingOptimizationArchePage() {
  return <AdvertisingOptimizationArcheContent />;
}
